import React, { PropsWithChildren, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '@components/Button'
import {
  Alert,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import BottomSheet from '@components/BottomSheet'
import ReviewMint from './ReviewMint'
import { formatMoney, formatNumber } from '@plug-core/utils/formatNumber'
import useMintCk from '@plug-core/hooks/useMintCk'
import { Titlebar } from '@components/Titlebar/Titlebar'
import { HiInformationCircle } from 'react-icons/hi'
import { TokenCard } from '@modules/Swap/ExchangeView'
import Prompt from '@components/Prompt'
import { PlugToken } from '@plug-core/interfaces/common'
import { BTC_ID, CKBTC_ICP_ID, ETH_ID } from '@plug-core/constants/tokenIds'
import useBalances from '@plug-core/hooks/useBalances'

const MintListItem = ({
  attribute,
  children,
}: PropsWithChildren & {
  attribute: string
}) => (
  <ListItem
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      my: 1,
    }}
  >
    <Typography variant="body2">{attribute}</Typography>
    <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
      <Typography variant="body2" color="text.secondary">
        {children}
      </Typography>
    </Stack>
  </ListItem>
)

const MintCK = () => {
  const { tokenId } = useParams<{ tokenId: string }>()
  const { t } = useTranslation()
  const [ckTokens, setCkTokens] = useState<PlugToken[]>()
  const { findBalance } = useBalances()

  const ckBTCBalance = findBalance({ id: CKBTC_ICP_ID })

  const {
    fee,
    token,
    setToken,
    amount,
    setAmount,
    loading,
    error,
    performMint,
    ckToken,
    mintSuccess,
    mintReviewVisible,
    setMintReviewVisible,
    ckerc20Tokens,
    tokens,
    mintMode,
    setMintMode,
    prompt,
    setPrompt,
    ordinals,
    maxAmount,
    updateCkBTCBalance,
    balanceLoading,
  } = useMintCk({ tokenId })

  const navigate = useNavigate()

  useEffect(() => {
    if (!mintSuccess) return

    const timeout = setTimeout(() => {
      setMintReviewVisible(false)
      navigate(-1)
    }, 3000)

    return () => clearTimeout(timeout)
  }, [mintSuccess])

  useEffect(() => {
    if (!ckerc20Tokens || !tokens) return

    const erc20 = ckerc20Tokens.map((ckToken) =>
      tokens.find((token) => token.address === ckToken.erc20_contract_address)
    )
    const eth = tokens.find((t) => t.id === ETH_ID)
    const btc = tokens.find((t) => t.id === BTC_ID)
    const combined = [eth, btc, ...erc20].filter((token) => !!token)
    setCkTokens(combined)
  }, [ckerc20Tokens, tokens])

  const onInfoClick = (event: any) => {
    event.stopPropagation()

    setPrompt({
      title: t(`mintCk.titleAbout`),
      description: t(`mintCk.about`),
    })
  }

  return (
    <Stack pb={1} pt={2}>
      <Titlebar
        title={t('mintCk.title')}
        onActionClick={{
          type: 'back',
          action: () => navigate(-1),
        }}
        leftItem={
          <IconButton sx={{ width: 40, marginRight: 1 }} color="primary" onClick={onInfoClick}>
            <HiInformationCircle size={40} />
          </IconButton>
        }
      />

      <Box borderBottom={1} borderColor="divider" mb={2} mt={0.75} mx={2.5}>
        <Tabs
          value={mintMode === 'mint' ? 0 : 1}
          onChange={(_e, index) => setMintMode(index === 0 ? 'mint' : 'unmint')}
        >
          <Tab label={t('mintCk.mint')} sx={{ width: '50%' }} />
          <Tab label={t('mintCk.unmint')} sx={{ width: '50%' }} />
        </Tabs>
      </Box>
      {mintMode === 'mint' ? (
        <Stack mx={2} spacing={1.25}>
          <TokenCard
            token={token}
            amount={amount}
            setAmount={setAmount}
            mode="mint"
            error={undefined}
            showBalance
            maxAmount={maxAmount}
            tokensForSelect={ckTokens || []}
            onTokenSelect={setToken}
          />

          <List component={Paper} disablePadding>
            <MintListItem attribute={t('mintCk.receiveAmount')}>
              {formatNumber(Number(amount || 0), ckToken?.symbol)}
            </MintListItem>

            {fee && (
              <MintListItem attribute={t('mintCk.gasFee')}>
                {formatNumber(fee.fee, token?.id === BTC_ID ? 'BTC' : 'ETH')} ={' '}
                {formatMoney(fee.feeMoney)}
              </MintListItem>
            )}

            <MintListItem attribute={t('mintCk.delay')}>{t('mintCk.delayTime')}</MintListItem>

            {token?.id === BTC_ID && (
              <MintListItem attribute={t('mintCk.ckBtcBalance')}>
                {formatNumber(ckBTCBalance?.balance, 'ckBTC')}
              </MintListItem>
            )}

            <Divider sx={{ borderColor: 'background.default' }} />
          </List>
          {ordinals.length > 0 && token?.id === BTC_ID && (
            <Alert severity="warning">
              <Typography variant="body2" fontWeight={500}>
                {t('send.excludedOrdinals', {
                  ordinals: ordinals.length,
                })}
              </Typography>
            </Alert>
          )}
          <Button
            variant="rainbow"
            disabled={!!error || loading}
            loading={loading}
            onClick={() => setMintReviewVisible(true)}
            fullWidth
          >
            {error || t('mintCk.review')}
          </Button>
          {token?.id === BTC_ID && (
            <Button
              variant="outlined"
              loading={balanceLoading}
              disabled={balanceLoading}
              onClick={updateCkBTCBalance}
              fullWidth
            >
              {t('mintCk.updateBalance')}
            </Button>
          )}
        </Stack>
      ) : (
        <Stack mx={2} my={4} alignItems="center" justifyContent="center">
          <Typography variant="h6" color="textSecondary" textAlign="center">
            Coming Soon
          </Typography>
        </Stack>
      )}

      <Prompt show={!!prompt} close={() => setPrompt(null)}>
        <Stack alignItems="center">
          <Stack alignItems="center" mb={0.5}>
            <Typography variant="h6" mt={0.2}>
              {prompt?.title}
            </Typography>
            <Typography
              textAlign="center"
              mt={1}
              mb={2}
              color="text.light"
              sx={{ whiteSpace: 'pre-line' }}
            >
              {prompt?.description}
            </Typography>
          </Stack>

          <Button onClick={() => setPrompt(null)} variant="outlined">
            {t('common.understood')}
          </Button>
        </Stack>
      </Prompt>

      <BottomSheet visible={mintReviewVisible} onBackdropClick={() => setMintReviewVisible(false)}>
        <ReviewMint
          fromToken={token}
          toToken={ckToken}
          amount={amount}
          receiveAmount={amount}
          loading={loading}
          performMint={performMint}
          mintSuccess={mintSuccess}
          kind="mint"
        />
      </BottomSheet>
    </Stack>
  )
}

export default MintCK
